import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ReactNode } from 'react'
import { Button } from './Button.tsx'
import { TableSkeleton } from './TableSkeleton.tsx'
import { icons } from './icons.ts'

/**
 * The one table.
 *
 * Every list in the product -- verifications, products, filiais, people -- was its
 * own markup with its own idea of a header, a hover, a loading row and an
 * error. This is the verification queue's table, which had the most thought put into
 * it, abstracted: sticky header, rows that light up under the pointer when
 * they open something, a skeleton while the first page arrives, a spinner
 * over faded rows while a later one does, and an error state *in the table*
 * with a retry -- instead of a red line above it that said "a requisição
 * falhou (500)" and left the person with nothing to do about it.
 */
export const TH =
  'sticky top-0 z-10 border-b border-stroke-weak bg-background-sunken px-3 py-3 text-left text-tPSemi text-text-strong'
export const TD = 'border-b border-stroke-weak px-3 py-3 text-tP text-text-strong align-middle'

export interface Column<T> {
  readonly key: string
  readonly header: ReactNode
  readonly render: (row: T) => ReactNode
  /** Extra classes on every cell of the column (`tabular-nums`, `font-mono`). */
  readonly className?: string
  readonly headerClassName?: string
}

export interface DataTableProps<T> {
  readonly columns: ReadonlyArray<Column<T>>
  readonly rows: readonly T[]
  readonly rowKey: (row: T) => string
  /** True while a fetch is in flight. Skeleton with no rows, overlay with some. */
  readonly loading?: boolean
  /** What went wrong fetching, when something did. Shown in the table, with a retry. */
  readonly error?: string | undefined
  readonly onRetry?: () => void
  /** What to say when there is nothing, once we know there is nothing. */
  readonly empty?: ReactNode
  readonly onRowClick?: (row: T) => void
  readonly rowClassName?: (row: T) => string | undefined
  /** How many skeleton rows to hold the height with. */
  readonly pageSize?: number
  readonly minWidth?: string
  /** A row after the last one, spanning every column: an inline add, a total. */
  readonly footer?: ReactNode
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  error,
  onRetry,
  empty = 'Nada aqui.',
  onRowClick,
  rowClassName,
  pageSize = 10,
  minWidth,
  footer,
}: DataTableProps<T>) {
  const firstLoad = loading && rows.length === 0
  const refreshing = loading && rows.length > 0
  const failed = error !== undefined && !loading

  return (
    <div
      className={`rounded-base border-stroke-weak bg-background-raised relative w-full overflow-auto border ${
        failed || (!loading && rows.length === 0 && footer === undefined) ? 'min-h-[20rem]' : ''
      }`}
    >
      <table
        className="w-full border-collapse"
        style={minWidth === undefined ? undefined : { minWidth }}
      >
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} scope="col" className={`${TH} ${column.headerClassName ?? ''}`}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={refreshing ? 'opacity-40 transition-opacity' : 'transition-opacity'}>
          {firstLoad && <TableSkeleton columns={columns.length} rows={pageSize} />}

          {rows.map((row) => {
            const clickable = onRowClick !== undefined
            return (
              <tr
                key={rowKey(row)}
                tabIndex={clickable ? 0 : undefined}
                role={clickable ? 'button' : undefined}
                onClick={clickable ? () => onRowClick(row) : undefined}
                onKeyDown={
                  clickable
                    ? (event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return
                        event.preventDefault()
                        onRowClick(row)
                      }
                    : undefined
                }
                className={[
                  clickable ? 'hover:bg-fill-weak cursor-pointer transition-colors' : '',
                  rowClassName?.(row) ?? '',
                ].join(' ')}
              >
                {columns.map((column) => (
                  <td key={column.key} className={`${TD} ${column.className ?? ''}`}>
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
        {footer !== undefined && (
          <tfoot>
            <tr>
              <td colSpan={columns.length} className="border-stroke-weak border-t px-3 py-2">
                {footer}
              </td>
            </tr>
          </tfoot>
        )}
      </table>

      {/* Statuses that replace the rows sit over the whole box, below the
          header, centred in it -- not inside one row, which centred them in
          the row and left the rest of the box empty. */}
      {failed && rows.length === 0 && (
        <TableStatus>
          <TableError message={error} onRetry={onRetry} />
        </TableStatus>
      )}
      {!firstLoad && !failed && rows.length === 0 && footer === undefined && (
        <TableStatus>
          <span className="text-tP text-text-weak">{empty}</span>
        </TableStatus>
      )}
      {refreshing && <TableOverlay />}
      {failed && rows.length > 0 && (
        <div className="border-stroke-weak bg-background-raised border-t px-3 py-2">
          <TableError message={error} onRetry={onRetry} compact />
        </div>
      )}
    </div>
  )
}

/** Fills the box below the sticky header and centres what it is given. */
export function TableStatus({ children }: { children: ReactNode }) {
  return (
    <div className="absolute inset-x-0 top-12 bottom-0 flex items-center justify-center p-3">
      {children}
    </div>
  )
}

/** A spinner over rows that are still there, faded: a refresh, not a blank. */
export function TableOverlay() {
  return (
    <div
      aria-live="polite"
      aria-busy="true"
      className="pointer-events-none absolute inset-0 flex items-start justify-center pt-16"
    >
      <span className="bg-background-raised text-icon-neutral rounded-full p-2 shadow-md">
        <FontAwesomeIcon icon={icons.busy} spin aria-label="Carregando" className="h-4 w-4" />
      </span>
    </div>
  )
}

/** What went wrong, and the one thing to do about it. */
export function TableError({
  message,
  onRetry,
  compact = false,
}: {
  message: string
  onRetry?: (() => void) | undefined
  compact?: boolean
}) {
  return (
    <div
      className={
        compact
          ? 'flex flex-wrap items-center gap-3'
          : 'flex w-full flex-col items-center justify-center gap-3 text-center'
      }
    >
      <span className="text-tP text-text-strong">Não foi possível carregar a lista.</span>
      <span className="text-tSmall text-text-weak">{message}</span>
      {onRetry !== undefined && (
        <Button
          variant="secondary"
          tone="neutral"
          size="small"
          leadingIcon={icons.refresh}
          onClick={onRetry}
        >
          Tentar de novo
        </Button>
      )}
    </div>
  )
}
